// src/pages/ImageViewer.tsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import './ImageView.css';
import { CardSidebar } from '../../component/CardGallery';
import { ImageProps, ImageView, ImageMetadata } from '../../component/ImagePreview';
import Loading from '../../component/Loading';

type ImageViewState = {
  items: ImageProps[];
  currentPage: number;
  totalPages: number;
  system_name?: string;
  from?: string;              // ← our custom prop
};

const ImageViewPage : React.FC = () => {
    const { imageId } = useParams<{ imageId: string }>();
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams();
    
    const [data, setData] = useState<ImageViewState | null>(location.state as ImageViewState | null);
    const [loading, setLoading] = useState<boolean>(!data);
    const [error, setError] = useState<string | null>(null);
    
    const [imageIdx, setImageIdx] = useState(0)

    useEffect(() => {
      const fetchData = async () => {
        if (!data) {
          setLoading(true);
          try {
            const systemName = searchParams.get('system_name');
            if (!systemName) {
              throw new Error("System name is missing from URL");
            }
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/image-details/${imageId}?system_name=${systemName}`,{
              credentials: 'include'
            });
            if (!response.ok) {
              throw new Error('Failed to fetch image details');
            }
            const imageData = await response.json();
            setData({
              items: [imageData],
              currentPage: 1,
              totalPages: 1,
              system_name: systemName,
              from: `/image-view?${searchParams.toString()}`
            });
          } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
          } finally {
            setLoading(false);
          }
        }
      };

      fetchData();
    }, [imageId, data, searchParams]);

    useEffect(() => {
        if (data?.items) {
            var idx = data.items.findIndex(item => `${item.id}` === imageId)
            if (idx === -1) {
                // Image not found in the current items, might need to fetch it
                return
            }
            setImageIdx(idx)
        }
    }, [imageId, data?.items])
    
    const updateDisposition = async (newDisposition: string) => {
      if (!data || !data.items[imageIdx]) {
        throw new Error('No image selected');
      }
      const image = data.items[imageIdx];

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/update_disposition`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            disposition: newDisposition,
            image_id: image.id,
            system_name: image.system_name || 'default_system',
          }),
        }
      );

      if (!res.ok) {
        // try to get a useful message from the server
        let errMessage = 'Failed to update disposition';
        try {
          const errBody = await res.json();
          errMessage = errBody.detail || errBody.message || errMessage;
        } catch { /* ignore parse errors */ }
        throw new Error(errMessage);
      }

      // if you need to update local UI state, do it here:
      // data.items[imageIdx].disposition = newDisposition;
    };


    function handleNavigate(id: number) {
      navigate(`/image-view/${id}?system_name=${data?.system_name}`, {state : location.state})
    }
    function handleBack() {
    if (data?.from) {
      // jump back to exactly that gallery URL
      navigate(data.from);
    } else {
      // fallback to browser back
      navigate(-1);
    }
  }

    const handlePrevious = () => {
      if (data && imageIdx > 0) {
        const prevImageId = data.items[imageIdx - 1].id;
        handleNavigate(prevImageId);
      }
    };
  
    const handleNext = () => {
      if (data && imageIdx < data.items.length - 1) {
        const nextImageId = data.items[imageIdx + 1].id;
        handleNavigate(nextImageId);
      }
    };

    if (loading) {
      return <Loading message="Loading image..." />;
    }

    if (error) {
      return <div className="text-red-500 text-center mt-10">{error}</div>;
    }

    if (!data || !data.items || data.items.length === 0) {
      return <div className="text-center mt-10">Image not found.</div>;
    }

    const handleDefectCommentUpdate = async (defectIndex: number, comment: string) => {
    try {
      const currentImage = data.items[imageIdx];
      if (currentImage.defects[defectIndex].comment === undefined )
      {
        throw new Error('DATASET_REQUIRED');
      }
      // API call to update the defect comment using the dataset-specific endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/images/${currentImage.id}/defects/${defectIndex}/comment`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comment }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update defect comment');
      }

      const result = await response.json();
      
      // Update local state with the returned annotation data
      const updatedItems = [...data.items];
      updatedItems[imageIdx] = {
        ...currentImage,
        defects: result.updated_annotation_data // Use the server's updated data
      };

      // Update your data state (adjust this based on your state management)
      setData(prev => {
        if (!prev) return null;
        
        return {
          ...prev,
          items: updatedItems,
          // Ensure required fields are preserved with proper types
          currentPage: prev.currentPage ?? 1,
          totalPages: prev.totalPages ?? 1,
          system_name: prev.system_name ?? '',
          from: prev.from ?? ''
        };
      });

    } catch (error) {
      console.error('Error updating defect comment:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

// Then update your return statement to include the new prop:
return (
  <>
    <div className='flex flex-row h-[90vh] bg-slate-700 overflow-hidden'>
      <CardSidebar 
        activeItem={data.items[imageIdx]} 
        data={data.items} 
        onNavigate={handleNavigate} 
      />
      
      <ImageView 
        image={data.items[imageIdx]} 
        updateDisposition={updateDisposition} 
        id={data.items[imageIdx].id}
        onDefectCommentUpdate={handleDefectCommentUpdate} // Add this new prop
      />
      
      <div className='flex flex-col items-center justify-center flex-1 h-full items-stretch pr-12 pt-4 pb-24'>
        <ImageMetadata 
          image={data.items[imageIdx]}
          mutable_disposition={true}
          updateDisposition={updateDisposition}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onBack={handleBack}
          hasPrevious={imageIdx > 0}
          hasNext={imageIdx < data.items.length - 1}
          className='flex flex-col bg-gray-300 px-2 py-4 rounded-lg border-4 border-blue-500 w-full h-full'
        />
      </div>
    </div>
  </>
);
}

export default ImageViewPage